import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination } from './ui';
import {
  useC360List, C360Table, ColumnChooser,
  ViewIcon, EditIcon, DeleteIcon, unwrapLookupItems,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/riskmasters';

const DATA_TYPES = [
  { value: 1, label: 'Para' },
  { value: 2, label: 'Sayı' },
  { value: 3, label: 'Tarih' },
  { value: 4, label: 'Yüzde' },
  { value: 5, label: 'Evet/Hayır' },
];
const dataTypeLabel = (v) => DATA_TYPES.find((t) => t.value === Number(v))?.label || '-';

function categoryLabel(item) {
  if (!item) return '';
  return item.description || item.textId || '';
}

export function RiskMastersPage() {
  usePageMeta('Risk Master Tanım', 'Kategori, tanım, açıklama ve veri tipi seçeneklerini yönetin.');

  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();
  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [categories, setCategories] = useState([]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // Risk kategorileri küçük bir lookup — tümü /Select ile yüklenir.
  useEffect(() => {
    fetch('/api/riskcategories/Select', { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setCategories(unwrapLookupItems(data)))
      .catch(() => {});
  }, []);

  const categoryName = useMemo(() => {
    const map = new Map(categories.map((c) => [c.id, categoryLabel(c)]));
    return (id) => (id ? map.get(id) || '-' : '-');
  }, [categories]);

  const categoryFilterOptions = useMemo(
    () => categories.map((c) => ({ value: c.id, label: categoryLabel(c) })),
    [categories],
  );

  const COLUMN_FILTER_META = useMemo(() => ({
    categoryId: { type: 'select', odataField: 'CategoryId', valueFormat: 'guid', options: categoryFilterOptions },
    textId: { type: 'text', odataField: 'TextId' },
    name: { type: 'text', odataField: 'Name' },
    description: { type: 'text', odataField: 'Description' },
    dataType: { type: 'select', odataField: 'DataType', options: DATA_TYPES },
    sortOrder: { type: 'number', odataField: 'SortOrder' },
  }), [categoryFilterOptions]);

  const SEARCH_FIELDS = useMemo(() => ['TextId', 'Name', 'Description'], []);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'SortOrder',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const openNew = () => navigate('/risk-masters/new');
  const openEdit = (row = selected) => { if (!row) return; navigate(`/risk-masters/${row.id}?edit=1`, { state: { row } }); };
  const openView = (row = selected) => { if (!row) return; navigate(`/risk-masters/${row.id}`, { state: { row } }); };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt silinemedi');
      showToast('success', 'Kayıt silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('categoryId', {
      header: 'Kategori', meta: { filterType: 'select' },
      cell: ({ row }) => categoryName(row.original.categoryId) || row.original.category?.description || '-',
    }),
    columnHelper.accessor('textId', {
      header: 'Kod', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('name', { header: 'Tanım', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('description', { header: 'Açıklama', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('dataType', {
      header: 'Veri Tipi', meta: { filterType: 'select' },
      cell: (info) => dataTypeLabel(info.getValue()),
    }),
    columnHelper.accessor('sortOrder', { header: 'Sıra', meta: { filterType: 'number' }, cell: (info) => info.getValue() ?? '-' }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openView(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [categoryName, canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('SortOrder', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const resolveFilterProps = (columnId, meta) => {
    if (columnId === 'categoryId') return { options: meta.options };
    return {};
  };

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { headerName: 'Kategori', valueGetter: ({ row }) => categoryName(row.categoryId) },
        { field: 'textId', headerName: 'Kod' },
        { field: 'name', headerName: 'Tanım' },
        { field: 'description', headerName: 'Açıklama' },
        { headerName: 'Veri Tipi', valueGetter: ({ row }) => dataTypeLabel(row.dataType) },
        { field: 'sortOrder', headerName: 'Sıra' },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `risk-master-tanimlari_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kod, tanım veya açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Risk Master Tanım"
        subtitle="Kategori, tanım, açıklama ve veri tipi seçeneklerini yönetin."
        actions={canAdd ? <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Risk Tanım</button> : null}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          resolveFilterProps={resolveFilterProps}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openView}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && selected && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>{selected.name || selected.textId}</strong> kaydını silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
